import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { useEffectOnce } from 'shared/library/use-effect-once';
import { Animal, Breed } from 'shared/@generated/models';
import { apiRoute } from 'shared/library/api-route';
import { notify } from 'shared/library/notify';
import { createRequestClient } from 'shared/library/api-client';
import { useUserStore } from 'shared/state/user';
import { useLocale } from 'shared/library/i18n/use-locale';

let breedsRequest: Promise<void> | null = null;

function normalizeBreeds(value: unknown): Breed[] {
  return Array.isArray(value) ? value : [];
}

const useBreedsStore = create(
  combine(
    {
      breeds: [] as Breed[],
      error: false,
      loading: false,
    },
    (set, _get) => ({
      setBreeds: (breeds: Breed[]) => {
        set({ breeds: normalizeBreeds(breeds), error: false });
      },
      setError: (error: boolean) => set({ error }),
      setLoading: (loading: boolean) => set({ loading }),
    }),
  ),
);

export default useBreedsStore;

export const useBreeds = () => {
  const breeds = useBreedsStore((state) => normalizeBreeds(state.breeds));
  const error = useBreedsStore((state) => state.error);
  const loading = useBreedsStore((state) => state.loading);
  const setBreeds = useBreedsStore((state) => state.setBreeds);
  const setError = useBreedsStore((state) => state.setError);
  const setLoading = useBreedsStore((state) => state.setLoading);
  const apiKey = useUserStore((state) => state.apiKey);
  const { languageCode, countryCode } = useLocale();
  const request = useMemo(
    () =>
      createRequestClient({
        languageCode,
        apiKey,
        headers: { 'X-Country': countryCode },
      }),
    [apiKey, countryCode, languageCode],
  );

  const fetchBreeds = useCallback(() => {
    if (useBreedsStore.getState().breeds.length > 0) return;

    if (breedsRequest) {
      return breedsRequest;
    }

    setError(false);
    setLoading(true);

    breedsRequest = request<{ breeds: Breed[] }>(apiRoute('breeds.index'))
      .then((response) => {
        setBreeds(normalizeBreeds(response.breeds));
      })
      .catch((err) => {
        setError(true);
        notify.error(err, 'breeds.ts - loadBreeds');
      })
      .finally(() => {
        breedsRequest = null;
        setLoading(false);
      });

    return breedsRequest;
  }, [request, setBreeds, setError, setLoading]);

  function breedById(id: number) {
    return breeds.find((breed) => breed.id === id);
  }

  useEffectOnce(() => {
    fetchBreeds();
  });

  return {
    error,
    loading,
    fetchBreeds,
    breeds,
    breedById,
  };
};

export const usePopularBreeds = (options: { animal?: Animal } = {}) => {
  const breeds = useBreedsStore((state) => normalizeBreeds(state.breeds));
  const animal = options.animal;

  return useMemo(() => {
    const filteredBreeds = animal ? breeds.filter((breed) => breed.animal === animal) : breeds;

    return [...filteredBreeds]
      .sort((a, b) => b.active_pets_count! - a.active_pets_count!)
      .slice(0, 10);
  }, [animal, breeds]);
};
